// Thin typed fetch wrapper over the CLMS HTTP API.
//
// Types flow from the backend OpenAPI document via `@clms/contracts` (F-2
// contract-first codegen) — DTOs are never hand-written here. The wrapper exists
// mainly to give every caller two things the E2E harness must be able to
// interrupt deterministically:
//   - an AbortSignal (request cancel / navigation away), and
//   - a single 401 -> "session expired" hook (expired token/session).

import type { paths } from "@clms/contracts";
import { config, resolveTenantSlug } from "../config";
import { getValidAccessToken } from "../auth/oidc";
import { selectedTenantSlug } from "../auth/tenantSelection";

/**
 * Authorization header for a request, AND the access token it carries. Resolves (and refreshes, when near
 * expiry) the Keycloak access token and returns `Authorization: Bearer …`. Both are empty/undefined when
 * no token is held — an unauthenticated call, or an E2E/email-link session that carries none — so the
 * request goes out as before and a real 401 still drives the session-expired hook.
 *
 * WHY THE TOKEN IS HANDED BACK and not just the header: so the 401 this request may come back with can be
 * judged against the session as it stands WHEN THE ANSWER ARRIVES rather than as it stood when the
 * question was asked. Only the second-factor challenge needs that, and ADR-0021 §6.2's answer to it is
 * why — that one is answered by REDIRECTING the whole document for a new authorization, so acting on a
 * refusal that has since been overtaken is not a wasted retry but a navigation away from a session that is
 * already fine.
 *
 * The window is real and small: the OIDC callback boots the app on the PREVIOUS login's tokens while its
 * own exchange is in flight, so for a few dozen milliseconds every request leaves carrying a token that is
 * about to be replaced. Measured on main, run 41334510350 (task 2625efef) — `POST
 * /api/profile/language/reconcile` left 48 ms before the token carrying `amr: ["pwd","otp"]` landed, and
 * its 401 drove a third re-authentication nobody answered.
 *
 * One resolution per request, so the string compared later is exactly the string the header carried.
 */
async function authHeaderWithToken(): Promise<{
  headers: Record<string, string>;
  token: string | undefined;
}> {
  const token = await getValidAccessToken();
  return { headers: token ? { Authorization: `Bearer ${token}` } : {}, token };
}

/**
 * The tenant hint the backend resolves the request against (`X-Tenant`, matching TenantHint/
 * TenantResolutionMiddleware). It is essential for the pre-session email-link calls (US-1.3), which carry
 * no bearer, so the backend can tell which tenant the learner is signing into; on an authenticated call of
 * a person who belongs to one tenant it simply agrees with the token's tenant. Sent on every call so
 * path-form (`/t/<slug>`) tenancy works without a subdomain.
 *
 * TWO SOURCES SINCE ADR-0018 §6.3, in this order and no other:
 *   1. the addressable tenant in the URL — a `/t/<slug>/` path or a per-tenant subdomain. It WINS, because
 *      an address naming a tenant is an explicit statement about which tenant the page is for, and a
 *      bookmarked one has to keep working;
 *   2. the person's own SELECTION (auth/tenantSelection.ts), which is what answers on the apex host, where
 *      the authenticated app has no slug in any URL today — the `/t/:slug/` routes are pre-session only.
 *
 * Empty when neither names a tenant, which is every single-tenant person on the apex host: the backend then
 * resolves the tenant the token carries, exactly as it did before any of this existed. The selection is
 * null for them too — nothing is stored until a person with memberships chooses (§6.1).
 */
function tenantHint(): Record<string, string> {
  const slug = resolveTenantSlug() ?? selectedTenantSlug();
  return slug ? { "X-Tenant": slug } : {};
}

/** Union of path strings the generated contract knows about. */
export type ApiPath = keyof paths;

/**
 * RFC 7807 problem-details body (ASP.NET Core `Results.Problem`). The API returns
 * these on 4xx/5xx; validation gates carry domain extensions alongside the base
 * fields (e.g. the publish gate's `missingFields`). Parsed best-effort so callers
 * can render *why* a request failed instead of a bare status code.
 */
export interface ProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  /** Extension members (e.g. `missingFields: string[]` from the publish gate). */
  [key: string]: unknown;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Parsed problem-details body when the server sent one. */
    readonly problem?: ProblemDetails,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Raised on HTTP 401 so the router can redirect to login (session expiry). */
export class SessionExpiredError extends ApiError {
  constructor() {
    super(401, "session_expired");
    this.name = "SessionExpiredError";
  }
}

/**
 * The problem-details `error` value a step-up challenge carries (US-1.4, audit-privacy-api.md §6):
 * the token is valid but not *fresh* enough for a sensitive action (e.g. right-to-be-forgotten).
 */
export const STEP_UP_CHALLENGE = "step_up_required";

/**
 * Raised on a 401 that is a step-up challenge rather than a dead session. Unlike
 * {@link SessionExpiredError} it does NOT trip the session-expired hook (no redirect to
 * /login): the caller re-authenticates (fresh Keycloak auth) and retries the same action.
 */
export class StepUpRequiredError extends ApiError {
  constructor(problem?: ProblemDetails) {
    super(401, STEP_UP_CHALLENGE, problem);
    this.name = "StepUpRequiredError";
  }
}

/**
 * The `reason` a step-up challenge carries when what is missing is a SECOND FACTOR rather than a fresh
 * `auth_time` (ADR-0021 §6.2, backend `StepUpReasons.SecondFactorRequired`).
 *
 * The two share the `step_up_required` code on purpose — both mean "re-authenticate and you may", and the
 * SPA answers both with the same Keycloak re-auth — but they arrive from different places and only one of
 * them has a page waiting for it:
 *
 *   * the FRESHNESS challenge belongs to one sensitive action. A page calls it, catches
 *     {@link StepUpRequiredError}, and offers the person a button (UsersPage, CoverageReportPage,
 *     PayrollExportPanel).
 *   * THIS one belongs to the SESSION. It comes from the door (TenantResolutionMiddleware) on ANY request
 *     a privileged session makes, including the first read a page does on mount, so there is no action to
 *     retry and no page that asked. Nothing would catch it, and the person would see a surface of failed
 *     reads with no way forward — which is why it drives the hook below instead.
 */
export const SECOND_FACTOR_REQUIRED = "second_factor_required";

/**
 * `tokenUsed` is the access token the refused request carried (see {@link authHeaderWithToken}), so the
 * handler can tell a refusal OF THIS SESSION from one of a token that has since been replaced.
 */
type SecondFactorRequiredHandler = (tokenUsed: string | undefined) => void;
let onSecondFactorRequired: SecondFactorRequiredHandler = () => {};

/**
 * Registered once by the app shell (src/auth/session.tsx, beside the 401 hook) so a second-factor
 * challenge from ANY call drives one re-authentication. Like {@link setSessionExpiredHandler} it is a hook
 * rather than a direct call because `auth/stepUp` imports this module, so this module cannot import it.
 */
export function setSecondFactorRequiredHandler(handler: SecondFactorRequiredHandler): void {
  onSecondFactorRequired = handler;
}

/**
 * The 401 step-up branch both response paths share. Trips the hook above when the challenge is about the
 * session's second factor, then raises {@link StepUpRequiredError} either way — so the page callers that
 * already catch the freshness challenge keep behaving exactly as they did.
 */
function throwStepUpRequired(
  problem: ProblemDetails | undefined,
  tokenUsed: string | undefined,
): never {
  if (problem?.reason === SECOND_FACTOR_REQUIRED) {
    onSecondFactorRequired(tokenUsed);
  }
  throw new StepUpRequiredError(problem);
}

/**
 * The problem-details `error` value a frozen tenant's refusal carries (decision f3ca7f9e,
 * TenantResolutionMiddleware): the whole organisation's access to Verso is suspended, so every
 * `/api` and `/scorm` call is refused identically — learner, pääkäyttäjä, HR, email-link session.
 * The same code rides on the public questionnaire link's 409
 * (`QuestionnaireErrorCodes.TenantSuspended`), which is NOT this error — see {@link TenantSuspendedError}.
 */
export const TENANT_SUSPENDED = "tenant_suspended";

/**
 * Raised on a **403** that says the tenant is frozen. It is deliberately NOT
 * {@link SessionExpiredError}: the session is perfectly valid, so expiring it would clear the
 * session, bounce to /login, let the person sign in again — and meet the same 403 on the next
 * call, forever. So this error drives its own hook (see {@link setTenantSuspendedHandler}), which
 * renders the terminal suspension view instead.
 *
 * Only the 403 is this error. The anonymous questionnaire submit answers **409** with the same
 * code (the route is exempt from the middleware and the capability token is NOT spent), and that
 * one stays a plain {@link ApiError}: its respondent has two things to be told — the answers were
 * not saved, and the same link works after the resume — and blanking their form to the terminal
 * view would say neither.
 */
export class TenantSuspendedError extends ApiError {
  constructor(problem?: ProblemDetails) {
    super(403, TENANT_SUSPENDED, problem);
    this.name = "TenantSuspendedError";
  }
}

/**
 * The problem-details `error` value an unbought application's refusal carries (ADR-0014 §7.1 step 3,
 * `ApplicationEntitlementGate`): the ORGANISATION has not bought the application this request is
 * addressed to, so every `/api` and `/scorm` call is refused identically — learner, pääkäyttäjä, HR.
 *
 * A sibling of {@link TENANT_SUSPENDED} and deliberately not the same code: a freeze is liftable by the
 * `/resume` the operator took away, and this is a purchase nobody has made. The reader's next act differs,
 * so the sentence has to.
 */
export const APPLICATION_NOT_ENTITLED = "application_not_entitled";

/**
 * Raised on a **403** that says the tenant has not bought this application. Like
 * {@link TenantSuspendedError} and for the same reason, it is NOT a {@link SessionExpiredError} — the
 * session is valid, so clearing it would bounce to /login, sign in successfully and meet the same 403
 * forever — and it is not a plain {@link ApiError} either: an ordinary 403 is a verdict about the CALLER
 * that the page it happened on can render, and this one answers every route of the tenant.
 */
export class ApplicationNotEntitledError extends ApiError {
  constructor(problem?: ProblemDetails) {
    super(403, APPLICATION_NOT_ENTITLED, problem);
    this.name = "ApplicationNotEntitledError";
  }
}

type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler = () => {};

/** Registered once by the app shell so a 401 anywhere drives one redirect. */
export function setSessionExpiredHandler(handler: SessionExpiredHandler): void {
  onSessionExpired = handler;
}

/**
 * Why the WHOLE tenant's access is withdrawn — the two refusals that are about the organisation rather
 * than about the caller, and that therefore answer every route alike. The reason is carried through to the
 * view because the two want different sentences: one is lifted by a `/resume`, the other by a purchase.
 */
export type TenantAccessWithdrawal = typeof TENANT_SUSPENDED | typeof APPLICATION_NOT_ENTITLED;

type TenantAccessWithdrawnHandler = (reason: TenantAccessWithdrawal) => void;
let onTenantAccessWithdrawn: TenantAccessWithdrawnHandler = () => {};

/**
 * Registered once, above the router (src/auth/tenantAccess.tsx), so a withdrawn tenant's 403 from ANY
 * call replaces the whole surface with the matching terminal view at once. There is nothing to show per
 * page in either state: no tenant-scoped read answers at all.
 */
export function setTenantAccessWithdrawnHandler(handler: TenantAccessWithdrawnHandler): void {
  onTenantAccessWithdrawn = handler;
}

/** The two codes that mean "the organisation, not you", and the error each is raised as. */
const WITHDRAWALS: Record<TenantAccessWithdrawal, (problem?: ProblemDetails) => ApiError> = {
  [TENANT_SUSPENDED]: (problem) => new TenantSuspendedError(problem),
  [APPLICATION_NOT_ENTITLED]: (problem) => new ApplicationNotEntitledError(problem),
};

/**
 * The 403 branch both response paths share: a tenant-level withdrawal gets its own error (and trips the
 * hook above with its reason); every other 403 is an ordinary refusal about the caller and stays an
 * {@link ApiError}, as it always was. `problem` is passed in because the body can only be read once.
 */
function throwForbidden(
  problem: ProblemDetails | undefined,
  method: string,
  path: string,
): never {
  const code = problem?.error;
  if (code === TENANT_SUSPENDED || code === APPLICATION_NOT_ENTITLED) {
    onTenantAccessWithdrawn(code);
    throw WITHDRAWALS[code](problem);
  }
  throw new ApiError(403, problem?.detail ?? `HTTP 403 for ${method} ${path}`, problem);
}

export interface RequestOptions extends Omit<RequestInit, "method" | "body"> {
  method?: string;
  body?: unknown;
  /** Cancels the request (tab navigation, component unmount, explicit abort). */
  signal?: AbortSignal;
  /**
   * Read a 401 on THIS call as an ordinary coded refusal (an {@link ApiError}) rather than as a dead
   * session. Off by default, and it must stay the exception: the whole point of the 401 hook is that a
   * caller never has to remember to handle an expired session.
   *
   * It exists because one family of endpoints answers 401 about a CAPABILITY instead of about the
   * session — ADR-0008's `token_invalid_or_used`, which the questionnaire respondent paths return for a
   * token that is forged, spent, expired or somebody else's. `submitAnonymousResponse` already has to
   * dodge the hook and says so beside its hand-built fetch ("a 401 on this path is a spent/forged
   * CAPABILITY, not a dead session, and bouncing the respondent to /login would lose the answers they are
   * holding"); it can do that because it carries no identity at all.
   *
   * ADR-0027's two test-round calls cannot: they are deliberately AUTHENTICATED (§5.1 — nothing they do is
   * written, and the caller must be proved to be the token's holder), so they need the bearer this wrapper
   * attaches AND the capability reading of a 401. Measured, not theorised: without this flag, opening
   * somebody else's test link signed the READER out — a perfectly live session cleared, and the person
   * bounced to /login — because the refusal was about the link and the hook read it as the session.
   */
  unauthorizedIsRefusal?: boolean;
}

export async function apiFetch<T = unknown>(
  path: ApiPath | (string & {}),
  {
    method = "GET",
    body,
    headers,
    signal,
    unauthorizedIsRefusal = false,
    ...rest
  }: RequestOptions = {},
): Promise<T> {
  const auth = await authHeaderWithToken();
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...tenantHint(),
      ...auth.headers,
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
    ...rest,
  });

  return handleResponse<T>(res, method, path, auth.token, unauthorizedIsRefusal);
}

/**
 * Multipart upload (`multipart/form-data`) — the SCORM import/preview endpoints
 * take an `IFormFile`, which `apiFetch`'s JSON body cannot express. The browser
 * sets the multipart boundary itself, so Content-Type is deliberately left unset.
 * Shares the 401 hook, the abort signal, and the problem-details error surface —
 * so aborting a preview/import mid-flight fails cleanly with no partial state.
 */
export async function apiUpload<T = unknown>(
  path: ApiPath | (string & {}),
  file: Blob,
  {
    fieldName = "file",
    method = "POST",
    signal,
  }: { fieldName?: string; method?: string; signal?: AbortSignal } = {},
): Promise<T> {
  const form = new FormData();
  form.append(fieldName, file);
  const auth = await authHeaderWithToken();
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    credentials: "include",
    // No Content-Type: the browser sets the multipart boundary itself.
    headers: { Accept: "application/json", ...tenantHint(), ...auth.headers },
    body: form,
    signal,
  });

  return handleResponse<T>(res, method, path, auth.token);
}

/**
 * Binary download (`GET`) — the report export endpoints return a CSV/PDF file, not JSON, so
 * `apiFetch`'s `JSON.parse` cannot express them. Shares the auth header, the 401 -> session-expiry
 * hook, and the problem-details error surface; the `signal` makes an in-flight export deterministically
 * cancellable (the "cancel the export download" interrupting-action path). Resolves to the raw `Blob`
 * so the caller can trigger a browser download without the wrapper assuming a file name.
 *
 * `headers` carries the step-up assertion on a retry: the compliance export is a step-up-gated
 * sensitive action (US-1.4), so this path distinguishes the challenge from a dead session exactly as
 * {@link handleResponse} does — a download that bounced the SPA to /login on a 401 would make the
 * challenge unanswerable, since the caller would never get to re-authenticate and retry.
 */
export async function apiDownload(
  path: ApiPath | (string & {}),
  { signal, headers }: { signal?: AbortSignal; headers?: Record<string, string> } = {},
): Promise<Blob> {
  const auth = await authHeaderWithToken();
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    method: "GET",
    credentials: "include",
    headers: { ...tenantHint(), ...auth.headers, ...headers },
    signal,
  });

  if (res.status === 401) {
    const problem = await readProblem(res);
    if (problem?.error === STEP_UP_CHALLENGE) {
      throwStepUpRequired(problem, auth.token);
    }
    onSessionExpired();
    throw new SessionExpiredError();
  }
  // A frozen tenant refuses an export exactly as it refuses everything else (decision f3ca7f9e).
  if (res.status === 403) {
    throwForbidden(await readProblem(res), "GET", path);
  }
  if (!res.ok) {
    const problem = await readProblem(res);
    throw new ApiError(res.status, problem?.detail ?? `HTTP ${res.status} for GET ${path}`, problem);
  }
  return res.blob();
}

/** Shared response handling: 401 -> session expiry, 403 tenant-level refusal -> its terminal view,
 *  4xx/5xx -> ApiError(+problem), else parse JSON. */
async function handleResponse<T>(
  res: Response,
  method: string,
  path: string,
  tokenUsed?: string,
  unauthorizedIsRefusal = false,
): Promise<T> {
  if (res.status === 401) {
    const problem = await readProblem(res);
    // A step-up challenge is not a dead session — the session is still valid, it just
    // needs a fresh re-auth for this one sensitive action. Surface it distinctly so the
    // caller can re-authenticate + retry instead of bouncing the whole app to /login.
    if (problem?.error === STEP_UP_CHALLENGE) {
      throwStepUpRequired(problem, tokenUsed);
    }
    // …and neither is a refusal ABOUT A CAPABILITY, on the one family of calls that asks for one
    // (`unauthorizedIsRefusal`, see RequestOptions). The session is untouched, so clearing it would sign
    // the reader out over somebody else's link — and leave them with no session to read the refusal with.
    if (unauthorizedIsRefusal) {
      throw new ApiError(
        401,
        problem?.detail ?? `HTTP 401 for ${method} ${path}`,
        problem,
      );
    }
    onSessionExpired();
    throw new SessionExpiredError();
  }
  // The TENANT is refused, not the caller rejected — frozen (decision f3ca7f9e) or lacking the
  // application (ADR-0014 §7.1 step 3): a distinct error and its own hook, so neither ever becomes a
  // session expiry and neither bounces to /login.
  if (res.status === 403) {
    throwForbidden(await readProblem(res), method, path);
  }
  if (!res.ok) {
    const problem = await readProblem(res);
    throw new ApiError(res.status, problem?.detail ?? `HTTP ${res.status} for ${method} ${path}`, problem);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Best-effort parse of a problem-details (or any JSON) error body; undefined when unavailable. */
async function readProblem(res: Response): Promise<ProblemDetails | undefined> {
  try {
    const text = await res.text();
    if (!text) return undefined;
    return JSON.parse(text) as ProblemDetails;
  } catch {
    return undefined; // non-JSON body — fall back to a status-code message
  }
}
